'use client';

import { inter400 } from '@/config/fonts';
import classNames from 'classnames';
import React from 'react';

/**
 * GenericLineItemView - A template component for displaying items in AssignmentList
 *
 * This is a generic example that can be extended or used as a reference when creating
 * custom LineItemView components for the AssignmentList component.
 *
 * Your custom LineItemView should:
 * 1. Accept `data`, `actions`, `className`, and `style` props
 * 2. Display the item's information in a clear, scannable format
 * 3. Render the `actions` prop (buttons for assign/unassign)
 * 4. Follow the design patterns from existing item views (UserItemView, JobItemView, etc.)
 *
 * Example custom implementation:
 *
 * ```tsx
 * interface MyItem {
 *   id: string;
 *   name: string;
 *   email: string;
 * }
 *
 * const MyCustomLineItemView = ({
 *   data,
 *   actions,
 *   className,
 *   style,
 * }: {
 *   data: MyItem;
 *   actions: React.ReactNode;
 *   className?: string;
 *   style?: React.CSSProperties;
 * }) => {
 *   return (
 *     <div className={classNames('flex items-center border-b px-3 py-2', className)} style={style}>
 *       <div className="flex-1">
 *         <div className="font-semibold">{data.name}</div>
 *         <div className="text-sm text-gray-500">{data.email}</div>
 *       </div>
 *       <div className="actions">{actions}</div>
 *     </div>
 *   );
 * };
 * ```
 */

interface GenericItem {
  id?: string;
  name?: string;
  title?: string;
}

interface GenericLineItemViewProps<T extends GenericItem> {
  data: T;
  actions: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const GenericLineItemView = <T extends GenericItem>({
  data,
  actions,
  className,
  style,
}: GenericLineItemViewProps<T>) => {
  // Extract displayable fields from the data
  const displayName = data.name || data.title || 'Unnamed Item';
  const displayId = data.id || '';

  return (
    <div
      className={classNames(
        'flex items-center justify-between border-b border-gray-200 bg-white px-3 py-2 hover:bg-gray-50',
        className
      )}
      style={style}>
      {/* Item Information */}
      <div className="flex-1">
        <div
          className={classNames(
            'text-sm font-semibold text-gray-900',
            inter400.className
          )}>
          {displayName}
        </div>
        {displayId && (
          <div className={classNames('text-xs text-gray-500', inter400.className)}>
            ID: {displayId}
          </div>
        )}
      </div>

      {/* Actions (Assign/Unassign buttons) */}
      <div className="actions flex items-center gap-2">{actions}</div>
    </div>
  );
};
